import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AerosyncEnvironment, initAeroSyncWidget } from 'aerosync-web-sdk'
import { CheckCircle2, Landmark, ShieldCheck } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import { payLinkApi } from '@/features/payments/api'
import { queryKeys } from '@/lib/query-keys'

/**
 * PUBLIC self-serve bank-linking page — opened on the CUSTOMER'S OWN PHONE via
 * QR/SMS from the POS (plan §4.2 PRIMARY flow). Token-authenticated, single-use,
 * 15-min TTL; no login, no dashboard chrome. Bank credentials are entered ONLY
 * inside the Aerosync widget (iframe) — never visible to store staff.
 */
export const Route = createFileRoute('/pay-link/$token')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: PayLinkPage,
})

type Step = 'intro' | 'otp' | 'widget' | 'done'

function PayLinkPage() {
  const { token } = Route.useParams()
  const [step, setStep] = useState<Step>('intro')
  const [otpCode, setOtpCode] = useState('')
  const [otpMessage, setOtpMessage] = useState<string | null>(null)
  const [mismatchName, setMismatchName] = useState<string | null>(null)
  const [linkedLabel, setLinkedLabel] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const info = useQuery({
    queryKey: queryKeys.payments.payLinkInfo(token),
    queryFn: () => payLinkApi.info(token),
    retry: false,
  })

  const start = useMutation({
    mutationFn: () => payLinkApi.start(token),
    onSuccess: (res) => {
      setActionError(null)
      setMismatchName(res.nameMismatch ? (res.aeropayUserName ?? 'a different name') : null)
      if (res.mfaRequired) {
        setOtpMessage(res.displayMessage)
        setStep('otp')
      } else {
        setStep('widget')
      }
    },
    onError: (err: Error) => setActionError(extractMessage(err)),
  })

  const confirmOtp = useMutation({
    mutationFn: () => payLinkApi.otp(token, otpCode.trim()),
    onSuccess: () => {
      setActionError(null)
      setStep('widget')
    },
    onError: (err: Error) => setActionError(extractMessage(err)),
  })

  const complete = useMutation({
    mutationFn: (connectionId: string) => payLinkApi.complete(token, connectionId),
    onSuccess: (res) => {
      setLinkedLabel(res.displayLabel)
      setStep('done')
    },
    onError: (err: Error) => setActionError(extractMessage(err)),
  })

  const launchWidget = useMutation({
    mutationFn: () => payLinkApi.aerosyncSession(token),
    onSuccess: (session) => {
      setActionError(null)
      // Launch inside the user-gesture call stack (OAuth banks open a popup).
      const widget = initAeroSyncWidget({
        elementId: 'aerosync-host',
        iframeTitle: 'Connect your bank',
        environment:
          session.environment === 'production'
            ? AerosyncEnvironment.Production
            : AerosyncEnvironment.Sandbox,
        token: session.token,
        aeroPassUserUuid: session.aeroPassUserUuid,
        ...(session.configurationId ? { configurationId: session.configurationId } : {}),
        // Raw iframe width/height attributes on the SDK's fixed overlay —
        // percentages are invalid and warp the frame; 375×688 is its default.
        style: { width: '375px', height: '688px', bgColor: '#000000', opacity: 0.6 },
        // SDK default z-index is 1 — keep the widget above app chrome.
        zIndex: '1000',
        onLoad: () => {},
        onEvent: () => {},
        onSuccess: (event) => complete.mutate(event.connectionId),
        onClose: () => {
          // Customer backed out — nothing saved; they can relaunch any time.
        },
        onError: () => setActionError('The bank connection window hit a problem — try again.'),
      })
      widget.launch()
    },
    onError: (err: Error) => setActionError(extractMessage(err)),
  })

  if (info.isLoading) return <RoutePending />
  if (info.isError || !info.data) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold">This link has expired</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Bank-link invitations are valid for 15 minutes. Ask the store to send a fresh one.
        </p>
      </Shell>
    )
  }
  if (info.data.completed || step === 'done') {
    return (
      <Shell>
        <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
        <h1 className="mt-3 text-xl font-semibold">You’re all set{info.data.customerFirstName ? `, ${info.data.customerFirstName}` : ''}!</h1>
        {linkedLabel ? <p className="mt-1 text-sm text-muted-foreground">{linkedLabel} is linked.</p> : null}
        <p className="mt-3 text-sm text-muted-foreground">
          You can pay by bank at {info.data.shopName} from now on. You can close this page.
        </p>
      </Shell>
    )
  }

  return (
    <Shell>
      {step === 'intro' && (
        <>
          <Landmark className="mx-auto h-10 w-10 text-primary" />
          <h1 className="mt-3 text-xl font-semibold">Hi {info.data.customerFirstName} 👋</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Link your bank once to use <span className="font-medium">Pay by Bank via Aeropay</span> at{' '}
            {info.data.shopName}. Secure, direct payments — link your bank and pay with one click.
          </p>
          <Button className="mt-5 w-full" size="lg" onClick={() => start.mutate()} disabled={start.isPending}>
            {start.isPending ? 'Starting…' : 'Get started'}
          </Button>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" /> The store never sees your bank login.
          </p>
          {/* Required consent per Aeropay Integration Agreement §6 */}
          <p className="mt-2 text-xs text-muted-foreground">
            By linking a bank you agree to Aeropay&apos;s{' '}
            <a
              href="https://www.aeropay.com/legal/terms-of-service---end-user"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://www.aeropay.com/legal/privacy-policy"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Privacy Policy
            </a>
            .
          </p>
        </>
      )}

      {step === 'otp' && (
        <>
          <h1 className="text-lg font-semibold">Enter your verification code</h1>
          {mismatchName ? (
            <p className="mt-2 rounded-md bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
              This phone number already has an Aeropay profile under{' '}
              <span className="font-medium">{mismatchName}</span>. If that&apos;s not you, stop here
              and ask the store to update your phone number.
            </p>
          ) : null}
          <p className="mt-2 text-sm text-muted-foreground">
            {/* Required copy per Aeropay launch checklist */}
            {otpMessage ?? 'Please enter the verification code sent to your phone or email.'}
          </p>
          <Input
            className="mt-4 text-center text-lg tracking-[0.4em]"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={8}
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value)}
            placeholder="••••••"
          />
          <Button
            className="mt-4 w-full"
            size="lg"
            onClick={() => confirmOtp.mutate()}
            disabled={confirmOtp.isPending || otpCode.trim().length < 4}
          >
            {confirmOtp.isPending ? 'Checking…' : 'Verify'}
          </Button>
        </>
      )}

      {step === 'widget' && (
        <>
          <h1 className="text-lg font-semibold">Connect your bank</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Choose your bank and sign in — your credentials go to your bank only, never to the store.
          </p>
          <Button
            className="mt-4 w-full"
            size="lg"
            onClick={() => launchWidget.mutate()}
            disabled={launchWidget.isPending || complete.isPending}
          >
            {launchWidget.isPending ? 'Opening…' : complete.isPending ? 'Finalizing…' : 'Choose my bank'}
          </Button>
          <div id="aerosync-host" className="mt-4" />
        </>
      )}

      {actionError ? (
        <p className="mt-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{actionError}</p>
      ) : null}
    </Shell>
  )
}

/** Minimal mobile-first shell — no dashboard chrome on a public page. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-start justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-border/60 bg-card p-6 text-center shadow-sm">
        {children}
      </div>
    </div>
  )
}

/** Backend 400s carry human copy (mapped APxxx) — strip the machine tag. */
function extractMessage(err: Error): string {
  const axiosLike = err as Error & { response?: { data?: { message?: string } } }
  const raw = axiosLike.response?.data?.message ?? err.message ?? 'Something went wrong — try again.'
  return raw.replace(/\s*\[AP\d+:[a-z_]+\]$/, '')
}
